/**
 * incomingInventoryColumns — die EINE Quelle der Spalten von `/incoming-inventory`.
 *
 * 🔴 Warum eine eigene Datei: die Seite (`IncomingInventory.jsx`, `renderTableHead`)
 * und ihr Ladegeruest (`Skeletons/IncomingInventorySkeleton.jsx`) muessen dieselben
 * Etiketten tragen. Traegt das Geruest Balken statt Text, ist seine Kopfzeile flacher
 * als die geladene, sobald ein Etikett umbricht — am 2026-10-06 gemessen: 39 px im
 * Ladezustand gegen 59 px geladen (R15 W1 / R16 W1). Zwei Listen waeren zwei Quellen,
 * und ein Import der Seite AUS dem Geruest waere ein Ringschluss (die Seite importiert
 * das Geruest bereits). Deshalb liegt die Liste darunter und beide lesen sie.
 *
 * 🔴 Welle 2 PR 3a (2026-10-06): jede Spalte trug danach eine feste `width`. Der
 * Resthoehensprung hatte zwei Ursachen — die Etiketten UND die inhaltsabhaengigen
 * Breiten. Mit `tableLayout: 'fixed'` und festen Breiten brach kein Etikett mehr um,
 * und beide Zustaende trugen dieselbe Kopfhoehe.
 *
 * 🔴 **G8b (UI2 Teil 10, 2026-10-21) loest die festen `width`-Zahlen ab.** Sie waren
 * eine ZWEITE Quelle neben `COLUMN_MAX[kind]` (`theme/tokens.js`) und liefen
 * auseinander: `product_name` bekam den Rest und mass bei 1440 ≈ 534 px, waehrend
 * `COLUMN_MAX.identity.max` 360 ist. Jede Spalte traegt jetzt `kind` und `priority`;
 * die Breite folgt aus der **Verteilung** (Hausform `POOverview.jsx:882-908`,
 * `ListenMuster.jsx:890-901`): `width_i = (max_i / spaltenBudget) × 100 %`, und die
 * Rollschwelle aus `min_i`. Deshalb steht hier KEINE px-Zahl mehr.
 *
 * `showVendorCol` / `showPoCol` kommen aus `groupBy` (Vorgabe `'vendor'`):
 *   'vendor'  → 8 Spalten (ohne Vendor)   ← Vorgabefall
 *   'po'      → 8 Spalten (ohne PO)
 *   'product' → 9 Spalten (mit beiden)
 *
 * 🔴 Die Reihenfolge dieses Arrays ist die SICHTBARE Spaltenreihenfolge. `priority`
 * ist eine Zuordnung, keine Umsortierung (Plan §2.7/W4): `useColumnPriority` gibt
 * die sichtbaren Spalten in EINGABEREIHENFOLGE zurueck.
 */
import { COLUMN_MAX } from './theme/tokens'

export function incomingInventoryColumns({ showVendorCol, showPoCol }) {
  return [
    { field: 'product_name', label: 'Product', kind: 'identity', priority: 'kern' },
    ...(showVendorCol ? [{ field: 'vendor_name', label: 'Vendor', kind: 'text', priority: 'lg' }] : []),
    ...(showPoCol ? [{ field: 'po_id', label: 'PO', kind: 'number', priority: 'lg' }] : []),
    { field: 'eta', label: 'ETA', kind: 'date', priority: 'kern' },
    { field: 'inbound_qty', label: 'Inbound', align: 'right', kind: 'number', priority: 'kern' },
    { field: 'current_on_hand', label: 'On Hand', align: 'right', kind: 'number', priority: 'lg' },
    { field: 'reorder_point', label: 'Reorder Pt.', align: 'right', kind: 'number', priority: 'xl' },
    { field: 'reorder_date', label: 'Reorder Date', kind: 'date', priority: 'xl' },
    // 🔴 KERNSPALTE seit G8b (Plan §2.7/W4): sie traegt „At risk". Als `lg` fiel sie
    // bei 1280 als ERSTE heraus, weil `vergleicheOptional` bei gleichem Rang und
    // gleichem `max` nach Eingabeindex sortiert und `po_id`/`current_on_hand` vor
    // ihr stehen. Sichtbar bleibt sie am ENDE der Menge (Array-Reihenfolge).
    { field: 'estimated_stockout_date', label: 'Est. Stockout', kind: 'date', priority: 'kern' },
  ]
}

// Der Vorgabefall `groupBy='vendor'` — fuer Aufrufer ohne `groupBy` und als
// Vorgabewert des Ladegeraests.
export const INCOMING_INVENTORY_DEFAULT_COLUMNS = incomingInventoryColumns({
  showVendorCol: false,
  showPoCol: true,
})

/** Die Deskriptoren fuer `useColumnPriority` / `TableViewport.priorities`.
 *
 *  Die Spaltenquelle traegt `field`; der Hook erwartet `key`. Diese eine
 *  Abbildung ist der Grund, warum es keine zweite Deskriptorliste gibt. */
export function incomingInventoryPriorities(columns) {
  return columns.map((c) => ({ key: c.field, priority: c.priority, kind: c.kind }))
}

/** Das Spaltenbudget = Σ `COLUMN_MAX[kind].max` ueber die SICHTBAREN Spalten.
 *  `?? 0`: eine Spalte mit unbekannter Art hat kein `max`, und EIN falscher
 *  Deskriptor darf nicht die ganze Tabelle reissen. */
function budget(visibleColumns) {
  return visibleColumns.reduce((summe, c) => summe + (COLUMN_MAX[c.kind]?.max ?? 0), 0)
}

/** Die ROLLSCHWELLE (§2.7a): die Breite, ab der die proportionale Verteilung
 *  JEDE Spalte ueber ihrem `COLUMN_MAX[kind].min` haelt.
 *
 *  Aus `Breite_i = (max_i / Budget) × T ≥ min_i` folgt
 *  `T ≥ min_i × Budget / max_i`; das Maximum ueber die sichtbaren Spalten ist die
 *  Schwelle. G8 hat KEINE Bedienspalte neben dem Budget (Plan §2.7a: `bedienSumme`
 *  = 0), deshalb steht hier kein Zuschlag. Darunter rollt der `TableContainer`
 *  (`overflow: auto`), statt die Kernspalten unter ihr Minimum zu quetschen.
 *
 *  🔴 Sie ersetzt die frueher hier stehende Summe der festen Breiten + 240: mit
 *  Prozentwerten ergaebe die `NaN` bzw. eine Konkatenation. */
export function incomingInventoryMinWidth(visibleColumns) {
  const spalten = Array.isArray(visibleColumns) ? visibleColumns : []
  const spaltenBudget = budget(spalten)
  if (spaltenBudget <= 0) return 0
  return spalten.reduce((schwelle, c) => {
    const { min, max } = COLUMN_MAX[c.kind] ?? {}
    if (!min || !max) return schwelle
    return Math.max(schwelle, (min * spaltenBudget) / max)
  }, 0)
}

/** Die Breitenverteilung der Tabelle — EINE Rechnung fuer Seite UND Geruest.
 *
 *  @param {Array<object>} visibleColumns die SICHTBAREN Spalten in Anzeigeordnung
 *  @param {number} containerWidth die rohe Innenbreite des Behaelters
 *    (`useContainerWidth`); ein nicht-endlicher Wert faellt auf 0 zurueck, und
 *    `tabellenMax` bleibt dann 0 — der Aufrufer rechnet seine Messbasis vorher
 *    aus (Hausform `POOverview.jsx:821`).
 *  @returns {{spaltenBudget:number, tabellenMax:number, spaltenMinBreite:number,
 *    tabellenIst:number, widthFor:function(string):(string|undefined)}}
 *    `widthFor(field)` ist der erklaerte Prozentwert der Spalte; bei
 *    `tabellenIst === spaltenBudget` genau `COLUMN_MAX[kind].max`, darunter nie
 *    groesser (PW7) und oberhalb der Rollschwelle nie kleiner als `min`.
 */
export function incomingInventoryLayout(visibleColumns, containerWidth) {
  const spalten = Array.isArray(visibleColumns) ? visibleColumns : []
  const spaltenBudget = budget(spalten)
  const spaltenMinBreite = incomingInventoryMinWidth(spalten)
  const breite = Number.isFinite(containerWidth) ? containerWidth : 0
  const tabellenMax = spaltenBudget > 0 ? Math.min(breite, spaltenBudget) : 0
  const tabellenIst = Math.max(tabellenMax, spaltenMinBreite)
  const widthFor = (field) => {
    if (spaltenBudget <= 0 || tabellenIst <= 0) return undefined
    const spalte = spalten.find((c) => c.field === field)
    const max = COLUMN_MAX[spalte?.kind]?.max ?? 0
    if (!max) return undefined
    return `${(max / spaltenBudget) * 100}%`
  }
  return { spaltenBudget, tabellenMax, spaltenMinBreite, tabellenIst, widthFor }
}
